import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Package, Plus, X, Check } from "lucide-react";

export function GameShowItemsPanel({ show, isHost, myParticipantId, itemCards }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cardId, setCardId] = useState("");

  const pool = show.items?.pool || [];

  const addItem = async () => {
    if (!name.trim()) return toast.error("Give the item a name");
    try {
      await api.post(`/game-shows/${show.id}/items`, { name, description, item_card_id: cardId || null });
      setName("");
      setDescription("");
      setCardId("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add");
    }
  };

  const remove = (id) => api.delete(`/game-shows/${show.id}/items/${id}`);

  const collect = async (item, participantId) => {
    try {
      await api.post(`/game-shows/${show.id}/items/${item.id}/collect`, { participant_id: participantId });
      toast.success(`${item.name} collected!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not collect");
    }
  };

  return (
    <div className="panel-wood rounded-2xl p-5 space-y-3" data-testid="gameshow-items-panel">
      <h2 className="font-display text-xl flex items-center gap-2"><Package className="w-4 h-4 text-[#D4AF37]" /> Item Collection</h2>
      {pool.length === 0 && <p className="text-xs text-[#f1e0c5]/30">No items in play yet — great for door/chest puzzle requirements too.</p>}
      {pool.map((item) => {
        const collector = (show.participants || []).find((p) => p.id === item.collected_by_participant_id);
        return (
          <div key={item.id} data-testid={`gameshow-item-${item.id}`} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm">{item.name}</p>
              {item.description && <p className="text-[10px] text-[#f1e0c5]/40">{item.description}</p>}
            </div>
            {collector ? (
              <span className="text-[10px] text-[#4d7c0f] flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> {collector.name}</span>
            ) : isHost ? (
              <div className="flex items-center gap-1.5 shrink-0">
                <Select onValueChange={(v) => collect(item, v)}>
                  <SelectTrigger data-testid={`gameshow-item-assign-${item.id}`} className="bg-black/30 border-white/10 h-7 text-xs w-32"><SelectValue placeholder="Give to…" /></SelectTrigger>
                  <SelectContent>{(show.participants || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
                <button data-testid={`gameshow-item-delete-${item.id}`} onClick={() => remove(item.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : myParticipantId ? (
              <Button data-testid={`gameshow-item-collect-${item.id}`} size="sm" className="rounded-full h-7 text-xs bg-[#3F9142] hover:bg-[#357a39] shrink-0" onClick={() => collect(item, myParticipantId)}>Collect</Button>
            ) : null}
          </div>
        );
      })}
      {isHost && (
        <div className="border-t border-white/10 pt-3 space-y-2">
          <Input data-testid="gameshow-item-name-input" placeholder="Item name (e.g. Golden Idol)" value={name} onChange={(e) => setName(e.target.value)} className="bg-black/30 border-white/10 text-sm" />
          <Input data-testid="gameshow-item-description-input" placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} className="bg-black/30 border-white/10 text-sm" />
          {itemCards?.length > 0 && (
            <Select value={cardId} onValueChange={setCardId}>
              <SelectTrigger data-testid="gameshow-item-card-select" className="bg-black/30 border-white/10 text-sm"><SelectValue placeholder="Link an Item Card (grants it on collect)…" /></SelectTrigger>
              <SelectContent>{itemCards.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button data-testid="gameshow-item-add-button" size="sm" className="rounded-full gap-1.5 bg-[#3F9142] hover:bg-[#357a39]" onClick={addItem}><Plus className="w-3.5 h-3.5" /> Add Item</Button>
        </div>
      )}
    </div>
  );
}
